import { supabase } from './supabase';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  featured_image: string | null;
  author_id: string | null;
  status: string | null;
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  view_count: number | null;
  reading_time: number | null;
  created_at: string | null;
  updated_at: string | null;
  author?: {
    id: string;
    name: string;
    avatar: string | null;
  };
  categories?: BlogCategory[];
  tags?: BlogTag[];
}

export interface BlogCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
}

export interface BlogTag {
  id: string;
  slug: string;
  name: string;
}

export interface BlogListParams {
  page?: number;
  limit?: number;
  category?: string;
  tag?: string;
  search?: string;
}

// Fetch published blog posts with pagination
export async function getPublishedPosts(params: BlogListParams = {}) {
  const { page = 1, limit = 12, category, tag, search } = params;
  const offset = (page - 1) * limit;

  let query = supabase
    .from('blog_posts')
    .select(`
      *,
      author:users!blog_posts_author_id_fkey(id, name, avatar),
      categories:blog_post_categories(
        category:blog_categories(*)
      ),
      tags:blog_post_tags(
        tag:blog_tags(*)
      )
    `, { count: 'exact' })
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .order('published_at', { ascending: false })
    .range(offset, offset + limit - 1);

  // Apply search filter
  if (search) {
    query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%,excerpt.ilike.%${search}%`);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  // Transform nested data
  let posts = data?.map(post => ({
    ...post,
    categories: post.categories?.map((c: any) => c.category) || [],
    tags: post.tags?.map((t: any) => t.tag) || []
  })) || [];

  // Apply category filter (post-processing)
  if (category) {
    posts = posts.filter(post => 
      post.categories.some((cat: any) => cat.slug === category)
    );
  }

  // Apply tag filter (post-processing)
  if (tag) {
    posts = posts.filter(post => 
      post.tags.some((t: any) => t.slug === tag)
    );
  }

  return {
    posts,
    totalCount: count || 0,
    totalPages: Math.ceil((count || 0) / limit),
    currentPage: page
  };
}

// Get a single blog post by slug
export async function getBlogPostBySlug(slug: string) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      author:users!blog_posts_author_id_fkey(id, name, avatar),
      categories:blog_post_categories(
        category:blog_categories(*)
      ),
      tags:blog_post_tags(
        tag:blog_tags(*)
      )
    `)
    .eq('slug', slug)
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .single();

  if (error) throw error;

  // Increment view count
  if (data) {
    await supabase
      .from('blog_posts')
      .update({ view_count: (data.view_count || 0) + 1 })
      .eq('id', data.id);
  }

  // Transform nested data
  return {
    ...data,
    categories: data.categories?.map((c: any) => c.category) || [],
    tags: data.tags?.map((t: any) => t.tag) || [],
    author: data.author || undefined
  } as BlogPost;
}

// Get all categories
export async function getCategories() {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('*')
    .order('name');

  if (error) throw error;
  return data;
}

// Get all tags
export async function getTags() {
  const { data, error } = await supabase
    .from('blog_tags')
    .select('*')
    .order('name');

  if (error) throw error;
  return data;
}

// Get recent posts for sidebar
export async function getRecentPosts(limit: number = 5) {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, slug, title, published_at')
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .order('published_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
}

// Get posts by category
export async function getPostsByCategory(categorySlug: string, params: BlogListParams = {}) {
  const category = await supabase
    .from('blog_categories')
    .select('id')
    .eq('slug', categorySlug)
    .single();

  if (category.error) throw category.error;

  const postIds = await supabase
    .from('blog_post_categories')
    .select('post_id')
    .eq('category_id', category.data.id);

  if (postIds.error) throw postIds.error;

  // TODO: Use the post IDs to filter results
  return getPublishedPosts(params);
}

// Get blog post by ID (for editing)
export async function getBlogPostById(id: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      author:users!blog_posts_author_id_fkey(id, name, avatar),
      categories:blog_post_categories(
        category:blog_categories(id, name, slug)
      ),
      tags:blog_post_tags(
        tag:blog_tags(id, name, slug)
      )
    `)
    .eq('id', id)
    .single();

  if (error || !data) return null;

  return {
    ...data,
    categories: data.categories?.map((c: any) => c.category) || [],
    tags: data.tags?.map((t: any) => t.tag) || [],
    author: data.author || undefined
  } as BlogPost;
}

// Create a new blog post
export async function createBlogPost(postData: Partial<BlogPost>): Promise<BlogPost | null> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('User not authenticated');

  const { tags, categories, author, category_id, ...post } = postData as any;

  // Calculate reading time if content exists
  const reading_time = post.content ? calculateReadingTime(post.content) : 1;

  // Create the post
  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      ...post,
      reading_time,
      author_id: userData.user.id,
      published_at: post.status === 'published' ? new Date().toISOString() : null
    })
    .select()
    .single();

  if (error) throw error;

  // Associate tags
  if (tags && Array.isArray(tags) && tags.length > 0) {
    const tagAssociations = tags.map((tagId: string) => ({
      post_id: data.id,
      tag_id: tagId
    }));

    await supabase
      .from('blog_post_tags')
      .insert(tagAssociations);
  }

  // Associate category - if categories array is provided, use the first one
  if (categories && Array.isArray(categories) && categories.length > 0) {
    await supabase
      .from('blog_post_categories')
      .insert({
        post_id: data.id,
        category_id: categories[0].id || categories[0]
      });
  }

  return data;
}

// Update an existing blog post
export async function updateBlogPost(id: string, postData: Partial<BlogPost>): Promise<BlogPost | null> {
  const { tags, categories, author, category_id, ...post } = postData as any;

  // Calculate reading time if content exists
  const reading_time = post.content ? calculateReadingTime(post.content) : undefined;

  // Update the post
  const { data, error } = await supabase
    .from('blog_posts')
    .update({
      ...post,
      ...(reading_time !== undefined && { reading_time }),
      published_at: post.status === 'published' && !post.published_at ? new Date().toISOString() : post.published_at
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // Update tags (delete existing and insert new)
  await supabase
    .from('blog_post_tags')
    .delete()
    .eq('post_id', id);

  if (tags && Array.isArray(tags) && tags.length > 0) {
    const tagAssociations = tags.map((tagId: string) => ({
      post_id: id,
      tag_id: tagId
    }));

    await supabase
      .from('blog_post_tags')
      .insert(tagAssociations);
  }

  // Update category
  await supabase
    .from('blog_post_categories')
    .delete()
    .eq('post_id', id);

  if (categories && Array.isArray(categories) && categories.length > 0) {
    await supabase
      .from('blog_post_categories')
      .insert({
        post_id: id,
        category_id: categories[0].id || categories[0]
      });
  }

  return data;
}

// Get posts by tag
export async function getPostsByTag(tagSlug: string, params: BlogListParams = {}) {
  const tag = await supabase
    .from('blog_tags')
    .select('id')
    .eq('slug', tagSlug)
    .single();

  if (tag.error) throw tag.error;

  const postIds = await supabase
    .from('blog_post_tags')
    .select('post_id')
    .eq('tag_id', tag.data.id);

  if (postIds.error) throw postIds.error;

  // TODO: Use the post IDs to filter results
  return getPublishedPosts(params);
}

// Delete blog post function
export async function deleteBlogPost(id: string) {
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Update reading time for all posts (utility function)
export async function updateAllReadingTimes() {
  const { data: posts, error } = await supabase
    .from('blog_posts')
    .select('id, content')
    .is('reading_time', null);

  if (error) throw error;
  if (!posts || posts.length === 0) return;

  for (const post of posts) {
    const reading_time = calculateReadingTime(post.content || '');
    await supabase
      .from('blog_posts')
      .update({ reading_time })
      .eq('id', post.id);
  }
}

// Helper functions
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

// Export for use in components
export function calculateReadingTime(content: string): number {
  const wordsPerMinute = 225;
  
  if (!content || content.trim().length === 0) return 1;
  
  // Remove markdown syntax and HTML tags for accurate word count
  const cleanContent = content
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove markdown links
    .replace(/#{1,6}\s/g, '') // Remove markdown headers
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic markdown
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
  
  const wordCount = cleanContent.split(/\s+/).filter(word => word.length > 0).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
}